import { z } from 'zod';
import { TimestampSchema, UidSchema } from './primitives.js';

/**
 * Prompt claims revocation — the mechanism ADR-0007 prescribed and nobody built.
 *
 * Firebase ID tokens carry custom claims and are cached for up to an hour. Until
 * this existed, demoting or removing a staff member left them holding their old
 * role until their token happened to refresh, with no way to force it. The
 * sharpest edge: `infra/firestore.rules` lets owner/manager write the staff
 * document, so a demoted manager could rewrite their own staff record inside
 * that window and restore their access.
 *
 * How it works:
 *   · `userClaims/{uid}.revokedAt` — a server-only watermark, epoch ms.
 *   · `claimsRefreshedAt` — the SAME number, stamped into the user's custom
 *     claims in the same operation.
 *   · A token whose `claimsRefreshedAt` is older than `revokedAt` is refused by
 *     `firestore.rules`.
 *
 * Why the watermark and not just a client-side forced refresh: rules evaluate
 * whatever token arrives on the wire. A stale-but-valid token passes for its
 * full lifetime no matter what any browser tab was asked to do — and an
 * uncooperative client simply declines. The client refresh ships alongside
 * this, but it is what makes the fix invisible to honest users, not what makes
 * it a security property.
 *
 * THE INVARIANT — breaking it is a fleet-wide lockout, not a bug:
 *
 *   `revokedAt` may only ever be advanced in the same operation that calls
 *   `setCustomUserClaims` with `claimsRefreshedAt` set to the same value.
 *
 * Advance the watermark without re-minting claims and the user is locked out
 * PERMANENTLY rather than for an hour, because no future token can ever satisfy
 * the comparison. So exactly one function writes either field
 * (`apps/functions/src/auth/claims-revocation.ts`), and inside it the claim
 * write goes FIRST and the Firestore write second: if the second fails, the
 * result is new claims with an un-advanced watermark, i.e. exactly today's
 * behaviour. The reverse order fails toward a locked door.
 *
 * An ABSENT `userClaims/{uid}` document means ALLOWED. That makes deployment a
 * no-op — no document exists for anyone on day one, so nobody is denied and no
 * backfill is needed — and costs nothing, because a client cannot create the
 * document (`allow write: if false`; the Admin SDK bypasses rules). A user
 * whose claims have never changed is therefore never checked, which is correct:
 * no revocation has been requested for them.
 */

export const USER_CLAIMS_COLLECTION = 'userClaims';

/** Why a user's claims were re-minted. Recorded for the support trail. */
export const ClaimsRevocationReasonSchema = z
  .enum(['role_changed', 'staff_removed', 'agent_status_changed', 'recruiter_status_changed'])
  .describe('What caused this re-mint; support reads it when a user reports losing access');

export const UserClaimsStateSchema = z.object({
  uid: UidSchema.describe('The user whose claims these are; equals the document id'),
  revokedAt: z
    .number()
    .int()
    .nonnegative()
    .describe(
      'Epoch ms. Tokens minted before this are refused by firestore.rules. Server-only — a client that could write this could un-revoke itself in one line.',
    ),
  claimsRefreshedAt: z
    .number()
    .int()
    .nonnegative()
    .describe(
      'Epoch ms stamped into the custom claims in the SAME operation. Always equal to revokedAt when written; kept separately so a mismatch is visible in support.',
    ),
  reason: ClaimsRevocationReasonSchema,
  updatedAt: TimestampSchema.describe('Server write time, for the support trail only'),
});

export type UserClaimsState = z.infer<typeof UserClaimsStateSchema>;
export type ClaimsRevocationReason = z.infer<typeof ClaimsRevocationReasonSchema>;

/**
 * The pure mirror of the rules expression, so the comparison is unit-testable
 * outside the emulator and reusable by a callable-side guard.
 *
 * Returns true when the token must be REFUSED.
 *
 * Two boundary decisions, both load-bearing:
 *   · Equal timestamps PASS. The claim is stamped with the same number as the
 *     watermark, so `>=` is what lets the freshly re-minted token through at
 *     all. A `>` here would lock out every user on their first re-mint.
 *   · A missing or non-numeric `claimsRefreshedAt` with a watermark present
 *     FAILS CLOSED. That combination means a token minted before this mechanism
 *     existed, presented after a revocation was requested — exactly the case
 *     the watermark is for.
 */
export function isTokenRevoked(tokenClaimsRefreshedAt: unknown, revokedAt: number): boolean {
  if (revokedAt <= 0) return false; // no revocation has ever been requested
  if (typeof tokenClaimsRefreshedAt !== 'number' || !Number.isFinite(tokenClaimsRefreshedAt)) {
    return true; // fail closed
  }
  return tokenClaimsRefreshedAt < revokedAt;
}
